/**
 * Terminal Store
 *
 * Zustand store for terminal panel state and session management.
 * Persists panel height and visibility across app restarts.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  TerminalSession,
  TerminalInitState,
  TerminalLargeDbDetectedEvent,
} from '../../../types/terminal';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';

/**
 * Helper to track terminal events by session type (fire-and-forget pattern for store contexts)
 */
function trackTerminalEvent(type: 'ai' | 'home'): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;

  const event = type === 'ai' ? 'feature:ai_workspace' : 'feature:ai_home_terminal';
  window.electronAPI.analytics.track(event).catch(() => {
    // Silently ignore analytics errors
  });
}

/** Default panel height in pixels */
const DEFAULT_PANEL_HEIGHT = 250;

/** Minimum panel height */
const MIN_PANEL_HEIGHT = 100;

/** Maximum panel height as percentage of viewport */
const MAX_PANEL_HEIGHT_PERCENT = 70;

interface TerminalState {
  /** Whether the terminal panel is visible */
  isVisible: boolean;
  /** Panel height in pixels */
  height: number;
  /** Whether panel is maximized */
  isMaximized: boolean;
  /** Active terminal sessions */
  sessions: TerminalSession[];
  /** Currently active session ID */
  activeSessionId: string | null;
  /** Pending request to create AI terminal (triggers connection selector flow) */
  pendingAiTerminalRequest: boolean;
  /** Large database detected event (for showing filter dialog) */
  largeDbEvent: TerminalLargeDbDetectedEvent | null;

  // Actions
  /** Toggle terminal panel visibility */
  togglePanel: () => void;
  /** Show terminal panel */
  showPanel: () => void;
  /** Hide terminal panel */
  hidePanel: () => void;
  /** Set panel height */
  setHeight: (height: number) => void;
  /** Toggle maximize state */
  toggleMaximize: () => void;
  /** Add a new terminal session */
  addSession: (session: TerminalSession) => void;
  /** Remove a terminal session */
  removeSession: (sessionId: string) => void;
  /** Set the active session */
  setActiveSession: (sessionId: string | null) => void;
  /** Rename a session */
  renameSession: (sessionId: string, title: string) => void;
  /** Get session by ID */
  getSession: (sessionId: string) => TerminalSession | undefined;
  /** Update init state for a session */
  updateInitState: (sessionId: string, initState: TerminalInitState | undefined) => void;
  /** Request creation of AI terminal (will trigger connection selector if needed) */
  requestAiTerminal: () => void;
  /** Clear pending AI terminal request */
  clearAiTerminalRequest: () => void;
  /** Set large database detected event */
  setLargeDbEvent: (event: TerminalLargeDbDetectedEvent | null) => void;
}

export const useTerminalStore = create<TerminalState>()(
  persist(
    (set, get) => ({
      isVisible: false,
      height: DEFAULT_PANEL_HEIGHT,
      isMaximized: false,
      sessions: [],
      activeSessionId: null,
      pendingAiTerminalRequest: false,
      largeDbEvent: null,

      togglePanel: () => {
        set((state) => ({ isVisible: !state.isVisible }));
      },

      showPanel: () => {
        set({ isVisible: true });
      },

      hidePanel: () => {
        set({ isVisible: false, isMaximized: false });
      },

      setHeight: (height: number) => {
        // Clamp height between min and max
        const clampedHeight = Math.max(MIN_PANEL_HEIGHT, height);
        set({ height: clampedHeight });
      },

      toggleMaximize: () => {
        set((state) => ({ isMaximized: !state.isMaximized }));
      },

      addSession: (session: TerminalSession) => {
        // Track terminal usage by session type
        trackTerminalEvent(session.type);

        set((state) => ({
          sessions: [...state.sessions, session],
          activeSessionId: session.id,
          isVisible: true, // Show panel when session is added
        }));
      },

      removeSession: (sessionId: string) => {
        set((state) => {
          const newSessions = state.sessions.filter((s) => s.id !== sessionId);
          let newActiveSessionId = state.activeSessionId;

          // If we're removing the active session, switch to another
          if (state.activeSessionId === sessionId) {
            newActiveSessionId = newSessions.length > 0 ? newSessions[0].id : null;
          }

          return {
            sessions: newSessions,
            activeSessionId: newActiveSessionId,
            // Hide panel if no sessions left
            isVisible: newSessions.length > 0 ? state.isVisible : false,
          };
        });
      },

      setActiveSession: (sessionId: string | null) => {
        set({ activeSessionId: sessionId });
      },

      renameSession: (sessionId: string, title: string) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId ? { ...s, title } : s
          ),
        }));
      },

      getSession: (sessionId: string) => {
        return get().sessions.find((s) => s.id === sessionId);
      },

      updateInitState: (sessionId: string, initState: TerminalInitState | undefined) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId ? { ...s, initState } : s
          ),
        }));
      },

      requestAiTerminal: () => {
        set({ pendingAiTerminalRequest: true, isVisible: true });
      },

      clearAiTerminalRequest: () => {
        set({ pendingAiTerminalRequest: false });
      },

      setLargeDbEvent: (event: TerminalLargeDbDetectedEvent | null) => {
        set({ largeDbEvent: event });
      },
    }),
    {
      name: 'sql-studio-terminal',
      // Crash-proof write (core/storage/guardedJsonStorage.ts): a full origin must not throw out of
      // set(). No toast here — this is panel geometry the user re-adjusts in one drag; the drop is
      // still reported to telemetry and the console.
      storage: createGuardedJsonStorage(),
      // Only persist panel height and visibility
      partialize: (state) => ({
        height: state.height,
        isVisible: state.isVisible,
      }),
    }
  )
);

// Export constants for use in components
export { DEFAULT_PANEL_HEIGHT, MIN_PANEL_HEIGHT, MAX_PANEL_HEIGHT_PERCENT };
